import { sortArrayOfObjects } from '../../../js/Helpers';

const GROUP_POSITIONS = {
	'1': {'0': { start:'1', end:'60' }},
	'2': {'0': { start:'1', end:'28' }, '1': { start:'32', end:'60' }},
	'3': {'0': { start:'1', end:'18' }, '1': { start:'21', end:'39' }, '2': { start:'42', end:'60' }},
	'4': {'0': { start:'1', end:'10' }, '1': { start:'17', end:'27' }, '2': { start:'33', end:'43' }, '3': { start:'50', end:'60' }}
};

export const DEFAULT_SCHEDULE_GRID_TIME_LABELS = [
	'7:00AM',
	'8:00AM',
	'9:00AM',
	'10:00AM',
	'11:00AM',
	'12:00PM',
	'1:00PM',
	'2:00PM',
	'3:00PM',
	'4:00PM'
];

export const SCHEDULE_GRID_DAY_START_MINUTES = 7 * 60;
export const SCHEDULE_GRID_DAY_END_MINUTES = 16 * 60;
export const SCHEDULE_GRID_CLICK_SNAP_MINUTES = 15;
export const SCHEDULE_GRID_DEFAULT_ITEM_DURATION_MINUTES = 60;

export const minutesToTimeString = (totalMinutes = SCHEDULE_GRID_DAY_START_MINUTES) => {
	const normalizedMinutes = Math.max(0, parseInt(totalMinutes, 10) || 0);
	const hours = Math.floor(normalizedMinutes / 60);
	const minutes = normalizedMinutes % 60;
	return `${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}:00`;
};

export const getScheduleGridClickTimeRange = ({
	relativeY = 0,
	gridHeight = 1,
	dayStartMinutes = SCHEDULE_GRID_DAY_START_MINUTES,
	dayEndMinutes = SCHEDULE_GRID_DAY_END_MINUTES,
	snapMinutes = SCHEDULE_GRID_CLICK_SNAP_MINUTES,
	defaultDurationMinutes = SCHEDULE_GRID_DEFAULT_ITEM_DURATION_MINUTES
} = {}) => {
	const safeGridHeight = Math.max(1, gridHeight);
	const clampedRelativeY = Math.max(0, Math.min(relativeY, safeGridHeight));
	const totalDayMinutes = Math.max(0, dayEndMinutes - dayStartMinutes);
	const rawMinutesFromTop = (clampedRelativeY / safeGridHeight) * totalDayMinutes;
	const snappedMinutesFromTop = Math.round(rawMinutesFromTop / snapMinutes) * snapMinutes;
	const startMinutes = Math.min(dayEndMinutes, Math.max(dayStartMinutes, dayStartMinutes + snappedMinutesFromTop));
	const endMinutes = Math.min(dayEndMinutes, startMinutes + defaultDurationMinutes);

	return {
		start_time: minutesToTimeString(startMinutes),
		end_time: minutesToTimeString(Math.max(startMinutes, endMinutes))
	};
};

export const timesOverlap = (time1, time2) => {
	const startTime1 = time1.start_time;
	const endTime1 = time1.end_time;
	const startTime1Array = startTime1.split(':');
	const endTime1Array = endTime1.split(':');
	const startTime1Number = parseInt(startTime1Array[0] + startTime1Array[1], 10);
	const endTime1Number = parseInt(endTime1Array[0] + endTime1Array[1], 10);

	const startTime2 = time2.start_time;
	const endTime2 = time2.end_time;
	const startTime2Array = startTime2.split(':');
	const endTime2Array = endTime2.split(':');
	const startTime2Number = parseInt(startTime2Array[0] + startTime2Array[1], 10);
	const endTime2Number = parseInt(endTime2Array[0] + endTime2Array[1], 10);

	return endTime1Number > startTime2Number && endTime2Number > startTime1Number;
};

export const addTimeGridMetrics = (items = [], lengthKey = 'grid_length') => {
	return items.reduce((results, item) => {
		const itemInfo = {...item};
		const startTimeArray = item.start_time.split(':');
		const endTimeArray = item.end_time.split(':');
		const rowStart = Math.max(1, (parseInt(startTimeArray[0], 10) - 7) * 60 + parseInt(startTimeArray[1], 10));
		const calculatedRowEnd = Math.max(1, (parseInt(endTimeArray[0], 10) - 7) * 60 + parseInt(endTimeArray[1], 10));
		const rowEnd = Math.max(rowStart + 1, calculatedRowEnd);

		itemInfo.row_start = rowStart;
		itemInfo.row_end = rowEnd;
		itemInfo[lengthKey] = rowEnd - rowStart;

		results.push(itemInfo);
		return results;
	}, []);
};

// Build one day's positioned items so each screen can share the same filter/clone/sort flow.
export const buildDaySchedule = ({
	items = [],
	dayNumber,
	filterItem = (item, tempDayNumber) => {
		if(`${item.day_number}` === `${tempDayNumber}`) return true;
		if(Array.isArray(item.days)) return item.days.map((day) => `${day}`).includes(`${tempDayNumber}`);
		return false;
	},
	lengthKey = 'grid_length',
	cloneItems = true
}) => {
	const dayItems = items.filter((item) => filterItem(item, dayNumber));
	const preparedItems = addTimeGridMetrics(
		cloneItems ? dayItems.map((item) => ({...item})) : dayItems,
		lengthKey
	);

	return sortArrayOfObjects(preparedItems, lengthKey, 'number', 'desc');
};

export const buildDayOverlays = ({
	overlays = [],
	dayNumber,
	filterOverlay = (overlay, tempDayNumber) => `${overlay.day_number}` === `${tempDayNumber}`,
	lengthKey = 'overlay_length',
	cloneOverlays = true
}) => {
	const dayOverlays = overlays.filter((overlay) => filterOverlay(overlay, dayNumber));
	const preparedOverlays = addTimeGridMetrics(
		cloneOverlays ? dayOverlays.map((overlay) => ({...overlay})) : dayOverlays,
		lengthKey
	);

	return sortArrayOfObjects(preparedOverlays, 'row_start', 'number', 'asc');
};

export const getTimeGridConflictKeys = (items = [], getItemKey = (item) => `${item.block_day_id || item.period_id || item.block_id || item.day_number || ''}`) => {
	const conflictKeys = new Set();

	for(let i = 0; i < items.length; i++)
	{
		for(let j = i + 1; j < items.length; j++)
		{
			if(!timesOverlap(items[i], items[j])) continue;
			conflictKeys.add(getItemKey(items[i]));
			conflictKeys.add(getItemKey(items[j]));
		}
	}

	return conflictKeys;
};

export const calculateTimeGridColumnStartEnd = (itemData, daySchedule, loggedPositions, startTime, endTime, idKey = 'block_id') => {
	const itemId = itemData[idKey];
	const itemTime = {[idKey]: itemId, start_time:startTime, end_time:endTime};

	let overlappingTimeGroups = [[itemTime]];
	for(let i = 0; i < daySchedule.length; i++)
	{
		const newItem = daySchedule[i];
		const newItemId = newItem[idKey];

		if(itemId === newItemId) continue;

		const newTime = {[idKey]: newItemId, start_time:newItem.start_time, end_time:newItem.end_time};
		if(!timesOverlap(itemTime, newTime)) continue;

		for(let j = 0; j < overlappingTimeGroups.length; j++)
		{
			const overlappingTimeGroup = overlappingTimeGroups[j];
			if(overlappingTimeGroup.findIndex((overlappingTime) => overlappingTime[idKey] === newItemId) !== -1) continue;

			let newTimeOverlapsEntireGroup = true;
			for(let k = 0; k < overlappingTimeGroup.length; k++)
			{
				if(!timesOverlap(overlappingTimeGroup[k], newTime)) newTimeOverlapsEntireGroup = false;
			}

			if(newTimeOverlapsEntireGroup) overlappingTimeGroups[j].push(newTime);
			else overlappingTimeGroups.push([itemTime, newTime]);
		}
	}

	const overallMaxStats = overlappingTimeGroups.reduce((results, overlappingTimeGroup) => {
		if(overlappingTimeGroup.length > results.max_overlaps) results = {max_overlaps: overlappingTimeGroup.length, overlapping_items: overlappingTimeGroup};
		return results;
	}, {max_overlaps:0, overlapping_items:[]});

	const possiblePositions = GROUP_POSITIONS[overallMaxStats.max_overlaps];
	const possiblePositionIndexes = Object.keys(possiblePositions);
	const usedPositions = overallMaxStats.overlapping_items.reduce((results, itemTimes) => {
		const overlappingItemId = itemTimes[idKey];
		const usedPosition = (overlappingItemId in loggedPositions) ? loggedPositions[overlappingItemId] : null;
		if(usedPosition) results.push(usedPosition);
		return results;
	}, []);
	const unusedPositions = possiblePositionIndexes.filter((positionIndex) => usedPositions.indexOf(positionIndex) === -1);
	const positionIndex = unusedPositions[0];

	return {
		position_index: positionIndex,
		col_start: possiblePositions[positionIndex].start,
		col_end: possiblePositions[positionIndex].end
	};
};
